import { Bind, Component, cleanProxy, delayAsync, propOf, registerComponent } from "@eusoft/webapp-core";
import { userInteraction } from "@eusoft/webapp-framework";
import { handleError } from "@eusoft/webapp-framework/helpers/Error";
import { useNetwork } from "@eusoft/webapp-framework/helpers/Network";
import { forModel } from "@eusoft/webapp-jsx";
import { Action, Content, ItemView, ListView, MaterialIcon, bestContrastWhiteOrBlack, contentInfo, formatCurrency, formatText } from "@eusoft/webapp-ui";
import { SwipeView } from "@eusoft/webapp-ui/components/SwipeView";
import { paymentService } from "../../abstraction/IPaymentService";
import { SteadyClick } from "../../behavoir/SteadyClick";
import { OrderItemType, OrderType, SalePointType, type Guid, type ICatalogFullView, type ICatalogGroupView, type ICreateOrderItem } from "../../entities/Commands";
import { Authorize } from "../../services/Authorize";
import { context } from "../../services/Context";
import { withSignManager } from "../../services/SignManager";
import { WithContext } from "../../services/WithContext";
import type { IComponentOptions } from "@eusoft/webapp-core";
import type { IAction, IContentOptions, ViewNode } from "@eusoft/webapp-ui";
import "./CreateOrderPage.scss";
import { executeAsync } from "../../services/DataService";

const DISCOUNT = /^-?\d+(?:\.\d+)?%$/;
const MANY_ITEMS = /^(-?\d+(?:\.\d+)?)x(\d+(?:\.\d+)?)$/;
const SIMPLE = /^-?\d+(?:\.\d+)?$/;


interface IProduct {
    productId: Guid;
    formatId: Guid;
    name: string;
    formatName: string;
    price: number;
    color: string;
}

interface IProductGroup {
    header: string;
    products: IProduct[];
}

interface ICatalogSelectorOptions {
    catalog: ICatalogFullView;
    onItemSelected: (item: IOrderItem) => unknown;
}

interface IButtonInfo {

    body?: ViewNode;
    value: string;
}

interface IOrderItem {

    total?: number;
    itemAmount: number;
    description?: string;
    quantity: number;
    isPercentage?: boolean;
    productId?: Guid;
    formatId?: Guid;
}

interface ICashRegisterOptions extends IComponentOptions {

    onNewItem: (item: IOrderItem) => void;
}

interface IState {
    activeViewIndex: number;
}

export class CashRegister extends Component<ICashRegisterOptions> {
    constructor(options: ICashRegisterOptions) {

        super();

        this.init(CashRegister, {
            template: forModel<this>(m => <div className={m.className}>
                <div className="current-line">
                    {m.currentLine}
                </div>
                <div className="buttons">
                    {m.buttons.forEach(b => <button>
                        <SteadyClick handler={() => m.onButtonClick(b.value)} />
                        {b.body ?? b.value}
                    </button>)}
                </div>
            </div>),
            ...options
        });

        this.buttons.push({ value: "1" });
        this.buttons.push({ value: "2" });
        this.buttons.push({ value: "3" });
        this.buttons.push({ value: "b", body: <MaterialIcon variant="outlined" name="backspace" /> });

        this.buttons.push({ value: "4" });
        this.buttons.push({ value: "5" });
        this.buttons.push({ value: "6" });
        this.buttons.push({ value: "x" });

        this.buttons.push({ value: "7" });
        this.buttons.push({ value: "8" });
        this.buttons.push({ value: "9" });
        this.buttons.push({ value: "%" });

        this.buttons.push({ value: "-" });
        this.buttons.push({ value: "0" });
        this.buttons.push({ value: "." });
        this.buttons.push({ value: "\n", body: <MaterialIcon variant="outlined" name="check" /> });

    }

    protected parseLine(line: string) {

        let result: IOrderItem;

        if (DISCOUNT.exec(line)) {

            result = {
                isPercentage: true,
                itemAmount: parseFloat(line),
                quantity: 1
            }
        }
        else if (SIMPLE.test(line)) {
            result = {
                itemAmount: parseFloat(line),
                quantity: 1
            }
        }
        else if (MANY_ITEMS.test(line)) {
            const grops = MANY_ITEMS.exec(line);
            result = {
                itemAmount: parseFloat(grops[2]),
                quantity: parseFloat(grops[1]),
            }
        }

        return result;
    }

    onButtonClick(value: string) {

        let validateLine = false;

        if (value == "b") {
            if (this.currentLine.length > 0)
                this.currentLine = this.currentLine.substring(0, this.currentLine.length - 1);
        }


        else if (value == "C") {
            this.currentLine = "";
        }
        else if (value == "\n") {
            validateLine = true;
        }
        else {
            const newLine = this.currentLine + value;
            if (this.parseLine(newLine) || (value == "." || value == "x") || (value == '-' && this.currentLine.length == 0)) {
                this.currentLine = newLine;
                if (value == "%")
                    validateLine = true;
            }
        }

        if (validateLine) {
            const result = this.parseLine(this.currentLine);
            if (result) {
                this.onNewItem(result);
                this.currentLine = "";
            }
        }
    }

    onNewItem(item: IOrderItem) {

    }

    currentLine: string = "";

    buttons: IButtonInfo[] = [];
}

export interface IOrderItemViewProps {
    value: IOrderItem;
    parent: CreateOrderPage;
}

export function OrderItemView(props: IOrderItemViewProps) {

    const actions = [{
        name: "delete",
        priority: "secondary",
        icon: <MaterialIcon name="delete" />,
        executeAsync: async () => props.parent.deleteItem(cleanProxy(props.value))
    }] as IAction[];

    return <ItemView
        maxActions={1} 
        content={props.value}
        primary={<>
            <span className="quantity">{
                props.value.isPercentage ?
                    `${props.value.itemAmount} %` :
                    `${props.value.quantity} x\u00A0` + (!props.value.productId ?
                        formatCurrency(props.value.itemAmount) : "")}</span>
            <span className="desc">{props.value.description ?? ""}</span>
            <span className="amount">{formatCurrency(props.value.total)}</span>
            
        </>}
        actions={actions} />
}

export function CatalogSelector(options: ICatalogSelectorOptions) {

    const state = {
        groups: [] as IProductGroup[]
    }

    const update = (catalog: ICatalogFullView) => {

        const visit = (view: ICatalogGroupView, grp: IProductGroup) => {

            if (view.products?.length <= 0)
                return;

            grp.header = view.name;
            grp.products = [];

            for (const viewProd of view.products) {
                for (const format of viewProd.formats) {
                    grp.products.push({
                            formatId: format.id,
                            formatName: viewProd.formats.length == 1 ? undefined : format.name,
                            name: viewProd.name,
                            price: format.price,
                            productId: viewProd.id,
                            color: viewProd.color ?? "var(--primary-color)"
                        })
           
                }
            }

            if (view.groups) {
                for (const viewGrp of view.groups)
                    visit(viewGrp, {} as IProductGroup);
            }

            state.groups.push(grp);
        }

        state.groups = [];

        if (catalog)
            visit(catalog.root, {} as IProductGroup);
    }

    const addProduct = (product: IProduct) => {
        options.onItemSelected({
            quantity: 1,
            description: product.name + (product.formatName ? " " + product.formatName : ""),
            itemAmount: product.price,
            productId: product.productId,
            formatId: product.formatId
        })
    }

    const computeStyle = (color: string) => {
        return "text-" + bestContrastWhiteOrBlack(color);
    }

    propOf(options, "catalog").subscribe(v => update(v));

    update(options.catalog);

    return <div className="catalog-selector">
        {state.groups.forEach(a => <section>
            <header>{a.header}</header>
            <div className="body">
                {a.products.forEach(b => <Action color={b.color} style={["contained", computeStyle(b.color)]} onExecuteAsync={()=>addProduct(b)}>
                    <span className="name">{b.name}</span>
                    <span className="format">{b.formatName}</span>
                    <span className="price">{formatCurrency(b.price)}</span>
                </Action>)}
            </div>
        </section>)}
    </div>
}

export class CreateOrderPage extends Content {

    private _orderId: string;
    private _state: IState;
    constructor() {

        super();

        this.init(CreateOrderPage, {
            title: "create-order",
            style: ["panel"],

            body: forModel(this, m => <>
                <ListView ref={m.itemsList}
                    name="item-list"
                    content={m.items}
                    createItemView={a => <OrderItemView parent={m} value={a} />} />
                <SwipeView activeIndex={Bind.twoWays(m.activeViewIndex)}>
                    <CashRegister onNewItem={m.onNewItem.bind(m)} />
                    <CatalogSelector onItemSelected={a => m.onNewItem(a) } catalog={m.catalog}/>
                </SwipeView>
                <div className="actions">
                    {m.total > 0 && <Action onExecuteAsync={() => m.payAsync()}>
                        Paga {formatCurrency(m.total)}
                    </Action>}
                </div>

            </>),
        } as IContentOptions<object>);

    }

    protected loadPageState() {

        const json = localStorage.getItem(this.name);

        this._state = json ? JSON.parse(json) : {} as IState;

        if (this._state.activeViewIndex !== undefined)
            this.activeViewIndex = this._state.activeViewIndex;
    }

    protected savePageState(state: Partial<IState>) {

        if (state)
            Object.assign(this._state, state);

        localStorage.setItem(this.name, JSON.stringify(this._state));
    }

    override async onOpenAsync() {

        this.loadPageState();

        this.onChanged("activeViewIndex", v => this.savePageState({ activeViewIndex: v }));
    }

    newOrder() {

        this._orderId = null;
        this.items.splice(0, this.items.length);
        this.compute();
    }

    override async onLoadAsync() {

        await this.refreshAsync();

        return true;
    }

    override async refreshAsync() {

        const salePoint = (await executeAsync("ListSalePoint", { ids: [context.salePoint?.id] })).items[0];

        if (salePoint?.activeCatalog?.id)
            this.catalog = await executeAsync("GetFullCatalog", { id: salePoint.activeCatalog.id });
        else
            this.catalog = undefined;       

        this.newOrder();
    }


    async payAsync() {

        const orderItems = this.items.map(a => ({
            productId: a.productId,
            formatId: a.formatId,
            quantity: !a.isPercentage ? a.quantity : 1,
            description: a.isPercentage ? a.itemAmount.toString() : a.description,
            unitAmount: !a.isPercentage ? a.itemAmount : a.total,
            type: a.isPercentage ? OrderItemType.Percentage : (a.productId ? OrderItemType.Product : OrderItemType.Amount),
            vatPercentage: 0
        } as ICreateOrderItem));


        if (!this._orderId) {
            this._orderId = await executeAsync("CreateOrder", {
                type: OrderType.Buy,
                merchantId: context.merchant.id,
                operatorId: context.operator?.id,
                salePointId: context.salePoint?.id,
                items: orderItems
            }); 

            if (!this._orderId)
                return;
        }
        else {
            await executeAsync("ChangeOrder", {
                id: this._orderId,
                operatorId: context.operator?.id,
                salePointId: context.salePoint?.id,
                items: orderItems
            });
        }


        const auth = await executeAsync("CreatePayment", {
            orderId: this._orderId,
            salePointId: context.salePoint.id,
            operatorId: context.operator?.id,
        })

        if (!auth)
            return;

        const card = await handleError(() => paymentService.readCardAsync({
            payToken: auth,
            amount: this.total,
            message: formatText("msg-wait-for-card-pay") as string
        }));

        if (!card)
            return;

        if (!card.payAuth) {
            userInteraction.messageAsync(formatText("err-pay-token-device"), "error", 4000);
            return;
        }

        const payRes = await useNetwork(() =>
            withSignManager(cid =>
                executeAsync("PayOrder", {
                    connectionId: cid,
                    cardToken: card.payAuth,
                    orderId: this._orderId,
                    salePointId: context.salePoint.id,
                    operatorId: context.operator?.id,
                })));

        if (!payRes)
            return;

        if (!payRes.isSuccess) {

            userInteraction.messageAsync(formatText(payRes.errorMessage, formatCurrency(payRes.cardBalance)), "error", 4000);
            return;
        }

        userInteraction.messageAsync(formatText("msg-order-payed-amount", formatCurrency(payRes.cardBalance)), "success", 4000);

        this.newOrder();
    }

    deleteItem(item: IOrderItem) {

        this.items.splice(this.items.indexOf(item), 1);
        this.compute();
    }

    protected async onNewItem(item: IOrderItem) {

        this.items.push(item);
        this.compute();
        await delayAsync(50);
        this.itemsList.scrollToLast();
    }

    protected compute() {

        let curTotal = 0;
        for (const item of this.items) {
            if (item.isPercentage)
                item.total = curTotal * item.itemAmount / 100;
            else
                item.total = item.itemAmount * item.quantity;

            curTotal += item.total;
        }

        this.total = curTotal;
    }

    total: number;

    items: IOrderItem[] = [];

    itemsList: ListView<IOrderItem>;

    catalog: ICatalogFullView;

    activeViewIndex: number = 0;

    static override info = contentInfo({

        name: "create-order",
        route: "/pay",
        icon: <MaterialIcon name="payments" />,
        factory: () => new CreateOrderPage(),
        features: [
            Authorize(a => a.businessAccount()
                .resource("Order", "create_buy")),
            WithContext("sale-point", SalePointType.Sell)
        ]
    });
}

registerComponent(CreateOrderPage, "CreateOrderPage");